import type { IntlUtils } from '$lib/intl.js';
import type { DpApInfoFr, DpApInfoEn } from './DpApTypes';
import { en } from './generated/dpapData.en';
import { fr } from './generated/dpapData.fr';

const defaultMapping: { fr: Omit<DpApInfoFr, 'p'>; en: Omit<DpApInfoEn, 'country'> } = {
    fr: {
        dp: 'du {p}',
        ap: 'au {p}',
        lp: '{p}',
        pays_citoyen: 'africain',
        pays_citoyens: 'africains',
        pays_citoyenne: 'africaine',
        pays_citoyennes: 'africaines',
        dv: 'des grandes villes',
        av: 'dans les grandes villes',
        v: 'grandes villes',
        dv2: '',
        av2: '',
        v2: '',
        dv3: '',
        av3: '',
        v3: '',
        dv4: '',
        av4: '',
        v4: '',
        dv5: '',
        av5: '',
        v5: '',
        dv6: '',
        av6: '',
        v6: '',
    },
    en: {
        main_city: 'big cities',
        city1: 'big cities',
        city2: '',
        city3: '',
        city4: '',
        city5: '',
        city6: '',
        country_national: 'african',
        country_nationals: 'africans',
    },
};

const _africaDpApInfo = {
    fr: {
        ...defaultMapping.fr,
        dp: "d'{p}",
        ap: 'en {p}',
        lp: "l'{p}",
    },
    en: {
        ...defaultMapping.en,
    },
} as const;

export class DpApHelper {
    constructor(private readonly intl: IntlUtils) {}

    private dpapFr(country: string | null): Record<string, string> {
        if (country == null) {
            country = '_africa';
        }

        const countryLabel = this.intl.withLocale('fr-FR').countryLabel(country);

        const partialInfo =
            fr[country.toUpperCase()] ??
            (country === '_africa' ? _africaDpApInfo.fr : {});
        const info = { ...defaultMapping.fr, p: countryLabel, ...partialInfo };

        return this.dpapToPlaceholders(countryLabel, info);
    }

    private dpapEn(country: string | null): Record<string, string> {
        if (country == null) {
            country = '_africa';
        }

        const countryLabel = this.intl.withLocale('en-US').countryLabel(country);

        const partialInfo =
            en[country.toUpperCase()] ??
            (country === '_africa' ? _africaDpApInfo.en : {});
        const info = { ...defaultMapping.en, country: countryLabel, ...partialInfo };

        return this.dpapToPlaceholders(countryLabel, info);
    }

    private dpapToPlaceholders(
        countryLabel: string,
        dpap: Record<string, string>,
    ): Record<string, string> {
        const results: Record<string, string> = {};
        for (const [key, value] of Object.entries(dpap)) {
            results[`{${key}}`] = value
                .replace('{p}', countryLabel)
                .replace('{country}', countryLabel);

            results[`%${key}%`] = value
                .replace('{p}', countryLabel)
                .replace('{country}', countryLabel);
        }

        return results;
    }

    private dpap(lang: string, country: string | null) {
        if (lang === 'en') {
            return this.dpapEn(country);
        } else {
            return this.dpapFr(country);
        }
    }

    /**
     * Replace placeholders in a text with the dp/ap placeholders
     * @param country Country code or "_africa" or null
     * @param text
     * @param lang
     */
    replaceDpAp(
        country: string | null,
        text: string,
        lang: string | null = null,
    ): string {
        const defaultLang = this.intl.getLang();

        const placeholders = this.dpap(lang ?? defaultLang, country);

        for (const [key, value] of Object.entries(placeholders)) {
            text = text.replaceAll(key, value);
        }

        return text;
    }
}
